import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Upload as UploadIcon,
  Link as LinkIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Analytics as AnalyticsIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
  Lightbulb as LightbulbIcon,
  TrendingUp as TrendingUpIcon,
  BugReport as BugReportIcon,
  Timeline as TimelineIcon,
  Groups as GroupsIcon,
  Compare as CompareIcon,
  Summarize as SummarizeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { dataAnalysisService } from '../services/dataAnalysisService';
import {
  DataAnalysisSession,
  DataAnalysisResult,
  DataSummary,
  ANALYSIS_TEMPLATES,
  AnalysisTemplate
} from '../models/dataAnalysis';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
};

const getTemplateIcon = (templateId: string) => {
  switch (templateId) {
    case AnalysisTemplate.GeneralInsights: return <LightbulbIcon />;
    case AnalysisTemplate.TrendAnalysis: return <TrendingUpIcon />;
    case AnalysisTemplate.AnomalyDetection: return <BugReportIcon />;
    case AnalysisTemplate.Correlation: return <TimelineIcon />;
    case AnalysisTemplate.Forecasting: return <AssessmentIcon />;
    case AnalysisTemplate.Segmentation: return <GroupsIcon />;
    case AnalysisTemplate.Comparison: return <CompareIcon />;
    case AnalysisTemplate.Summary: return <SummarizeIcon />;
    default: return <AnalyticsIcon />;
  }
};

const DataAnalysisStudio = () => {
  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<DataAnalysisSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<DataAnalysisSession | null>(null);
  
  // Create session dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [sessionTitle, setSessionTitle] = useState('');
  const [analysisType, setAnalysisType] = useState<'rss' | 'csv'>('csv');
  const [rssUrl, setRssUrl] = useState('');
  const [csvData, setCsvData] = useState('');
  const [dataSummary, setDataSummary] = useState<DataSummary | null>(null);
  
  // Analysis settings
  const [analysisPrompt, setAnalysisPrompt] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<AnalysisTemplate | ''>('');
  const [temperature, setTemperature] = useState(0.7);
  const [showTemplates, setShowTemplates] = useState(true);
  
  useEffect(() => {
    loadSessions();
  }, []);
  
  const loadSessions = () => {
    const allSessions = dataAnalysisService.getAllSessions();
    setSessions(Object.values(allSessions));
  };
  
  const handleCreateSession = () => {
    if (!sessionTitle.trim()) {
      setError('Please provide a session title');
      return;
    }
    
    if (analysisType === 'rss' && !rssUrl.trim()) {
      setError('Please provide an RSS URL');
      return;
    }
    
    if (analysisType === 'csv' && !csvData.trim()) {
      setError('Please provide CSV data');
      return;
    }
    
    try {
      const session = dataAnalysisService.createSession(
        sessionTitle,
        analysisType,
        analysisType === 'csv' ? csvData : undefined,
        analysisType === 'rss' ? rssUrl : undefined
      );
      
      setCreateDialogOpen(false);
      resetCreateForm();
      loadSessions();
      setSelectedSession(session);
      setActiveTab(analysisType === 'rss' ? 1 : 0);
    } catch (err: any) {
      setError(err.message || 'Failed to create session');
    }
  };
  
  const resetCreateForm = () => {
    setSessionTitle('');
    setRssUrl('');
    setCsvData('');
    setDataSummary(null);
  };
  
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setCsvData(content);
        try {
          const summary = dataAnalysisService.parseDelimitedData(content);
          setDataSummary(summary);
        } catch (err) {
          setError('Failed to parse CSV data');
        }
      };
      reader.readAsText(file);
    }
  };
  
  const handleAnalyze = async () => {
    if (!selectedSession || !analysisPrompt.trim()) {
      setError('Please provide an analysis prompt');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await dataAnalysisService.performAnalysis(
        selectedSession.sessionId,
        analysisPrompt,
        temperature
      );
      
      // Reload session to get updated analyses
      const updatedSession = dataAnalysisService.getSession(selectedSession.sessionId);
      if (updatedSession) {
        setSelectedSession(updatedSession);
      }
      
      setAnalysisPrompt('');
      setSelectedTemplate('');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to perform analysis');
    } finally {
      setLoading(false);
    }
  };
  
  const handleTemplateSelect = (templateId: AnalysisTemplate) => {
    const template = ANALYSIS_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(templateId);
      setAnalysisPrompt(template.prompt);
      setShowTemplates(false);
    }
  };
  
  const handleExportSession = (format: 'markdown' | 'json') => {
    if (!selectedSession) return;
    
    try {
      let content: string;
      let filename: string;
      
      if (format === 'markdown') {
        content = dataAnalysisService.exportSessionAsMarkdown(selectedSession.sessionId);
        filename = `${selectedSession.title.replace(/\s+/g, '_')}_analysis.md`;
      } else {
        content = dataAnalysisService.exportSessionAsJSON(selectedSession.sessionId);
        filename = `${selectedSession.title.replace(/\s+/g, '_')}_analysis.json`;
      }
      
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to export session');
    }
  };
  
  const handleDeleteSession = (sessionId: string) => {
    dataAnalysisService.deleteSession(sessionId);
    loadSessions();
    if (selectedSession?.sessionId === sessionId) {
      setSelectedSession(null);
    }
  };
  
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between" 
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={{ xs: 2, sm: 0 }}
          sx={{ mb: { xs: 2, sm: 3 } }}
        >
          <Typography 
            variant="h4"
            sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}
          >
            Data Analysis Studio
          </Typography>
          <Button
            variant="contained"
            startIcon={!isMobile && <AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44
            }}
          >
            {isMobile ? 'New Session' : 'New Analysis Session'}
          </Button>
        </Stack>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Tabs 
          value={activeTab} 
          onChange={(_, value) => setActiveTab(value)}
          variant={isMobile ? 'scrollable' : 'standard'}
          {...(isMobile && { scrollButtons: 'auto' })}
        >
          <Tab 
            label={isMobile ? 'CSV' : 'CSV/Data Analysis'}
            icon={<Badge badgeContent={sessions.filter(s => s.analysisType === 'csv').length} color="primary">
              <TableChartIcon fontSize={isMobile ? 'small' : 'medium'} />
            </Badge>} 
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minHeight: { xs: 44, sm: 48 }
            }}
          />
          <Tab 
            label={isMobile ? 'RSS' : 'RSS Feed Analysis'}
            icon={<Badge badgeContent={sessions.filter(s => s.analysisType === 'rss').length} color="primary">
              <LinkIcon fontSize={isMobile ? 'small' : 'medium'} />
            </Badge>} 
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              minHeight: { xs: 44, sm: 48 }
            }}
          />
        </Tabs>
      </Paper>
      
      {/* CSV Analysis Tab */}
      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {sessions.filter(s => s.analysisType === 'csv').map(session => (
            <Grid item xs={12} md={6} lg={4} key={session.sessionId}>
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="start">
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography 
                        variant="h6"
                        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                      >
                        {session.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                      >
                        {session.analyses.length} analyses performed
                      </Typography>
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteSession(session.sessionId)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" gap={0.5}>
                    <Chip 
                      label="CSV Data" 
                      size="small" 
                      icon={<TableChartIcon />}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                    <Chip 
                      label={new Date(session.updatedAt).toLocaleDateString()} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: { xs: 1, sm: 2 } }}>
                  <Button 
                    size="small" 
                    onClick={() => setSelectedSession(session)}
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    Open
                  </Button>
                  <Button 
                    size="small" 
                    onClick={() => handleExportSession('markdown')}
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    Export
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>
      
      {/* RSS Analysis Tab */}
      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={3}>
          {sessions.filter(s => s.analysisType === 'rss').map(session => (
            <Grid item xs={12} md={6} lg={4} key={session.sessionId}>
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="start">
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography 
                        variant="h6"
                        sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                      >
                        {session.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        noWrap
                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                      >
                        {session.sourceUrl}
                      </Typography>
                    </Box>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteSession(session.sessionId)}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" gap={0.5}>
                    <Chip 
                      label="RSS Feed" 
                      size="small" 
                      icon={<LinkIcon />}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                    <Chip 
                      label={`${session.analyses.length} analyses`} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  </Stack>
                </CardContent>
                <CardActions sx={{ p: { xs: 1, sm: 2 } }}>
                  <Button 
                    size="small" 
                    onClick={() => setSelectedSession(session)}
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    Open
                  </Button>
                  <Button 
                    size="small" 
                    onClick={() => handleExportSession('markdown')}
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    Export
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>
      
      {/* Create Session Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 }
          }
        }}
      >
        <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h6"
            sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
          >
            {isMobile ? 'New Session' : 'Create New Analysis Session'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Session Title"
              value={sessionTitle}
              onChange={(e) => setSessionTitle(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Analysis Type
              </InputLabel>
              <Select
                value={analysisType}
                onChange={(e) => setAnalysisType(e.target.value as 'rss' | 'csv')}
                label="Analysis Type"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                <MenuItem 
                  value="csv"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  CSV/Delimited Data
                </MenuItem>
                <MenuItem 
                  value="rss"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  RSS Feed
                </MenuItem>
              </Select>
            </FormControl>
            
            {analysisType === 'rss' && (
              <TextField
                fullWidth
                label="RSS Feed URL"
                value={rssUrl}
                onChange={(e) => setRssUrl(e.target.value)}
                placeholder="https://example.com/feed.rss"
                InputProps={{
                  startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
            )}
            
            {analysisType === 'csv' && (
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={!isMobile && <UploadIcon />}
                  fullWidth
                  sx={{ 
                    mb: 2,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 44
                  }}
                >
                  {isMobile ? 'Upload CSV' : 'Upload CSV File'}
                  <input
                    type="file"
                    accept=".csv,.txt"
                    hidden
                    onChange={handleFileUpload}
                  />
                </Button>
                
                <TextField
                  fullWidth
                  multiline
                  rows={isMobile ? 4 : 6}
                  label="Or paste CSV data here"
                  value={csvData}
                  onChange={(e) => {
                    setCsvData(e.target.value);
                    try {
                      const summary = dataAnalysisService.parseDelimitedData(e.target.value);
                      setDataSummary(summary);
                    } catch {
                      setDataSummary(null);
                    }
                  }}
                  placeholder="Name,Age,City&#10;John,25,New York&#10;Jane,30,London"
                  InputProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                  }}
                />
                
                {dataSummary && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography 
                      variant="body2"
                      sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                    >
                      Data Preview: {dataSummary.rowCount} rows, {dataSummary.columnCount} columns
                    </Typography>
                    <Typography 
                      variant="caption"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      Columns: {dataSummary.columns.map(c => c.name).join(', ')}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={() => setCreateDialogOpen(false)}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateSession} 
            variant="contained"
            disabled={!sessionTitle.trim() || (analysisType === 'rss' ? !rssUrl.trim() : !csvData.trim())}
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minHeight: 44,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Create Session
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Analysis Session Detail */}
      {selectedSession && (
        <Dialog
          open={!!selectedSession}
          onClose={() => setSelectedSession(null)}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: { xs: 0, sm: 2 }
            }
          }}
        >
          <DialogTitle sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between" 
              alignItems={{ xs: 'start', sm: 'center' }}
              spacing={{ xs: 1, sm: 0 }}
            >
              <Box>
                <Typography 
                  variant="h6"
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
                >
                  {selectedSession.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                >
                  {selectedSession.analysisType === 'rss' ? selectedSession.sourceUrl : 'CSV Data Analysis'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <IconButton 
                  onClick={() => handleExportSession('markdown')}
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <DownloadIcon fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
                <IconButton 
                  onClick={() => setSelectedSession(null)}
                  sx={{ minWidth: 44, minHeight: 44 }}
                >
                  <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
              </Stack>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Analysis Input */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
              >
                New Analysis
              </Typography>
              
              {showTemplates && (
                <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                  >
                    Select a template or write your own prompt:
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    {ANALYSIS_TEMPLATES.map(template => (
                      <Grid item xs={12} sm={6} md={4} key={template.id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                          onClick={() => handleTemplateSelect(template.id)}
                        >
                          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                            <Stack 
                              direction="row" 
                              spacing={1} 
                              alignItems="center" 
                              sx={{ mb: 1 }}
                            >
                              {getTemplateIcon(template.id)}
                              <Typography 
                                variant="subtitle2"
                                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                              >
                                {template.name}
                              </Typography>
                            </Stack>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              {template.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 2 : 3}
                label="Analysis Prompt"
                value={analysisPrompt}
                onChange={(e) => setAnalysisPrompt(e.target.value)}
                placeholder="What would you like to analyze?"
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                }}
              />
              
              <Stack 
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2} 
                alignItems={{ xs: 'stretch', sm: 'center' }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                  >
                    Temperature: {temperature}
                  </Typography>
                  <Slider
                    value={temperature}
                    onChange={(_, value) => setTemperature(value as number)}
                    min={0}
                    max={1}
                    step={0.1}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showTemplates}
                      onChange={(e) => setShowTemplates(e.target.checked)}
                    />
                  }
                  label="Show templates"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <PlayIcon />)}
                  onClick={handleAnalyze}
                  disabled={loading || !analysisPrompt.trim()}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    minHeight: 44,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Analyze
                </Button>
              </Stack>
            </Paper>
            
            {/* Previous Analyses */}
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}
            >
              Analysis History
            </Typography>
            {selectedSession.analyses.length === 0 ? (
              <Alert 
                severity="info"
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                No analyses performed yet. Start by running an analysis above.
              </Alert>
            ) : (
              <Stack spacing={{ xs: 2, sm: 3 }}>
                {selectedSession.analyses.map((analysis, index) => (
                  <Paper key={analysis.id} sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack 
                      direction="row" 
                      justifyContent="space-between" 
                      alignItems="start" 
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography 
                          variant="subtitle1" 
                          gutterBottom
                          sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                        >
                          Analysis {selectedSession.analyses.length - index}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                        >
                          {new Date(analysis.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleCopyToClipboard(analysis.analysis)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <CopyIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Stack>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
                      >
                        <strong>Prompt:</strong> {analysis.prompt}
                      </Typography>
                    </Alert>
                    
                    <Typography 
                      variant="body1" 
                      paragraph 
                      style={{ whiteSpace: 'pre-wrap' }}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {analysis.analysis}
                    </Typography>
                    
                    {analysis.insights.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography 
                          variant="subtitle2" 
                          gutterBottom
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          Key Insights:
                        </Typography>
                        <List dense>
                          {analysis.insights.map((insight, i) => (
                            <ListItem key={i} sx={{ py: { xs: 0.5, sm: 1 } }}>
                              <ListItemText 
                                primary={insight}
                                primaryTypographyProps={{
                                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                    
                    {analysis.recommendations && analysis.recommendations.length > 0 && (
                      <Box>
                        <Typography 
                          variant="subtitle2" 
                          gutterBottom
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          Recommendations:
                        </Typography>
                        <List dense>
                          {analysis.recommendations.map((rec, i) => (
                            <ListItem key={i} sx={{ py: { xs: 0.5, sm: 1 } }}>
                              <ListItemText 
                                primary={rec}
                                primaryTypographyProps={{
                                  sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
};

export default DataAnalysisStudio;